import * as React from 'react';
import { createRoute, Link } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { formatInr, paise } from '@bits/domain';
import type { BudgetKind, ProjectStatus } from '@bits/shared/zod';
import { rootRoute } from './__root';
import { AppShell } from '@/components/AppShell';
import { api } from '@/lib/api';
import { errorMessage as toMessage } from '@/lib/errors';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Select } from '@/components/ui/select';
import { Field } from '@/components/ui/field';
import { TaskBoard } from '@/components/TaskBoard';
import { ProjectEditDialog } from '@/components/ProjectEditDialog';
import { ProjectRenewalsTab } from '@/components/ProjectRenewalsTab';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';

export const projectDetailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/projects/$projectId',
  component: ProjectDetailPage,
});

type Tab = 'tasks' | 'allocations' | 'budget' | 'renewals';

const TABS: readonly Tab[] = ['tasks', 'allocations', 'budget', 'renewals'];

function initialTab(): Tab {
  const wanted = new URLSearchParams(window.location.search).get('tab');
  return TABS.find((t) => t === wanted) ?? 'tasks';
}

function ProjectDetailPage(): JSX.Element {
  const { projectId } = projectDetailRoute.useParams();
  const [tab, setTab] = React.useState<Tab>(initialTab);
  const [editOpen, setEditOpen] = React.useState(false);

  const projectQuery = useQuery({
    queryKey: ['project', projectId],
    queryFn: () => api.getProject(projectId),
  });

  const project = projectQuery.data;

  return (
    <AppShell>
      <div className="flex flex-wrap items-start justify-between gap-4 lg:flex-nowrap">
        <div className="flex min-w-0 flex-col gap-2">
          <Link
            to="/projects"
            className="text-xs font-medium text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))]"
          >
            ← Projects
          </Link>
          <h1 className="font-display text-3xl font-semibold tracking-tight md:text-4xl">
            {project ? `${project.code} · ${project.name}` : 'Loading…'}
          </h1>
          {project ? (
            <div className="flex items-center gap-2 text-sm text-[hsl(var(--muted-foreground))]">
              <Badge variant={project.status === 'active' ? 'default' : 'muted'}>
                {project.status}
              </Badge>
              <span>· {project.type}</span>
              <span>· {project.openTaskCount} open tasks</span>
            </div>
          ) : null}
        </div>
        {project && project.status !== 'closed' ? (
          <Button variant="outline" onClick={() => setEditOpen(true)}>
            Edit project
          </Button>
        ) : null}
      </div>
      {project ? (
        <ProjectEditDialog project={project} open={editOpen} onOpenChange={setEditOpen} />
      ) : null}

      <div className="flex gap-2 border-b border-[hsl(var(--border))]">
        <TabButton active={tab === 'tasks'} onClick={() => setTab('tasks')}>
          Tasks
        </TabButton>
        <TabButton active={tab === 'allocations'} onClick={() => setTab('allocations')}>
          Allocations
        </TabButton>
        <TabButton active={tab === 'budget'} onClick={() => setTab('budget')}>
          Budget & Status
        </TabButton>
        <TabButton active={tab === 'renewals'} onClick={() => setTab('renewals')}>
          Hosting & renewals
        </TabButton>
      </div>

      {tab === 'tasks' ? <TasksTab projectId={projectId} /> : null}
      {tab === 'allocations' ? <AllocationsTab projectId={projectId} /> : null}
      {tab === 'budget' ? <BudgetTab projectId={projectId} /> : null}
      {tab === 'renewals' ? <ProjectRenewalsTab projectId={projectId} /> : null}
    </AppShell>
  );
}

function TabButton({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <button
      type="button"
      onClick={onClick}
      className={
        'border-b-2 px-4 py-2 text-sm transition-colors ' +
        (active
          ? 'border-[hsl(var(--brand))] text-[hsl(var(--foreground))] font-medium'
          : 'border-transparent text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))]')
      }
    >
      {children}
    </button>
  );
}

type TaskFormValues = {
  title: string;
  description: string;
  assigneeId: string;
  dueOn: string;
  parentTaskId: string;
};

function TasksTab({ projectId }: { projectId: string }): JSX.Element {
  const queryClient = useQueryClient();
  const [dialogOpen, setDialogOpen] = React.useState(false);
  const [errorMessage, setErrorMessage] = React.useState<string | null>(null);

  const tasksQuery = useQuery({
    queryKey: ['tasks', projectId],
    queryFn: () => api.listProjectTasks(projectId),
  });
  const peopleQuery = useQuery({
    queryKey: ['people-simple'],
    queryFn: () => api.listPeopleSimple(),
  });

  const form = useForm<TaskFormValues>({
    defaultValues: { title: '', description: '', assigneeId: '', dueOn: '', parentTaskId: '' },
  });

  const createMutation = useMutation({
    mutationFn: (v: TaskFormValues) =>
      api.createTask(projectId, {
        title: v.title,
        description: v.description.trim() || undefined,
        assigneeId: v.assigneeId,
        dueOn: v.dueOn || undefined,
        parentTaskId: v.parentTaskId || undefined,
      }),
    onSuccess: async () => {
      setDialogOpen(false);
      setErrorMessage(null);
      form.reset();
      await queryClient.invalidateQueries({ queryKey: ['tasks', projectId] });
      await queryClient.invalidateQueries({ queryKey: ['project', projectId] });
      await queryClient.invalidateQueries({ queryKey: ['my-tasks'] });
    },
    onError: (err) => {
      setErrorMessage(toMessage(err));
    },
  });

  const [templateOpen, setTemplateOpen] = React.useState(false);
  const [templateAssignee, setTemplateAssignee] = React.useState('');
  const [templatePicked, setTemplatePicked] = React.useState<Record<string, boolean>>({});
  const projectQuery = useQuery({
    queryKey: ['project', projectId],
    queryFn: () => api.getProject(projectId),
  });
  const templatesQuery = useQuery({
    queryKey: ['task-templates', projectQuery.data?.type],
    queryFn: () => api.getTaskTemplates(projectQuery.data!.type),
    enabled: !!projectQuery.data,
  });
  const templateMutation = useMutation({
    mutationFn: async () => {
      const titles = (templatesQuery.data ?? [])
        .map((t) => t.title)
        .filter((title) => templatePicked[title]);
      for (const title of titles) {
        await api.createTask(projectId, { title, assigneeId: templateAssignee });
      }
      return titles.length;
    },
    onSuccess: async () => {
      setTemplateOpen(false);
      setTemplatePicked({});
      setErrorMessage(null);
      await queryClient.invalidateQueries({ queryKey: ['tasks', projectId] });
      await queryClient.invalidateQueries({ queryKey: ['project', projectId] });
    },
    onError: (err) => setErrorMessage(toMessage(err)),
  });
  const people = peopleQuery.data ?? [];
  const rootTasks = tasksQuery.data ?? [];

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-[hsl(var(--muted-foreground))]">
          Depth limited to two levels (task + subtask). Server rejects deeper trees.
        </p>
        <div className="flex gap-2">
          {(templatesQuery.data ?? []).length > 0 ? (
            <Dialog
              open={templateOpen}
              onOpenChange={(open) => {
                setTemplateOpen(open);
                if (open) {
                  const all: Record<string, boolean> = {};
                  for (const t of templatesQuery.data ?? []) {
                    all[t.title] = !rootTasks.some((r) => r.title === t.title);
                  }
                  setTemplatePicked(all);
                }
              }}
            >
              <DialogTrigger asChild>
                <Button size="sm" variant="outline">
                  Add from template
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Add template tasks</DialogTitle>
                  <DialogDescription>
                    Standard tasks for this project type. Titles already on the board are unticked.
                  </DialogDescription>
                </DialogHeader>
                <div className="flex flex-col gap-3">
                  <Field label="Assignee" required>
                    <Select
                      value={templateAssignee}
                      onChange={(e) => setTemplateAssignee(e.currentTarget.value)}
                    >
                      <option value="">Select…</option>
                      {people.map((p) => (
                        <option key={p.id} value={p.id}>
                          {p.name}
                        </option>
                      ))}
                    </Select>
                  </Field>
                  <ul className="flex flex-col gap-2">
                    {(templatesQuery.data ?? []).map((t) => (
                      <li key={t.title}>
                        <label className="flex items-center gap-2 text-sm">
                          <input
                            type="checkbox"
                            checked={templatePicked[t.title] ?? false}
                            onChange={(e) =>
                              setTemplatePicked((s) => ({
                                ...s,
                                [t.title]: e.currentTarget.checked,
                              }))
                            }
                          />
                          {t.title}
                        </label>
                      </li>
                    ))}
                  </ul>
                  {errorMessage ? (
                    <Alert variant="destructive">
                      <AlertTitle>Create failed</AlertTitle>
                      <AlertDescription>{errorMessage}</AlertDescription>
                    </Alert>
                  ) : null}
                </div>
                <DialogFooter>
                  <Button
                    onClick={() => templateMutation.mutate()}
                    disabled={
                      templateMutation.isPending ||
                      templateAssignee === '' ||
                      !Object.values(templatePicked).some(Boolean)
                    }
                  >
                    {templateMutation.isPending ? 'Adding…' : 'Add tasks'}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          ) : null}
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button size="sm">New task</Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>New task</DialogTitle>
                <DialogDescription>
                  Pick a parent to make this a subtask. Only top-level tasks can have subtasks.
                </DialogDescription>
              </DialogHeader>
              <form
                onSubmit={form.handleSubmit((v) => createMutation.mutate(v))}
                className="flex flex-col gap-3"
              >
                <Field label="Title" required error={form.formState.errors.title?.message}>
                  <Input {...form.register('title', { required: 'Enter a title' })} />
                </Field>
                <div className="flex flex-col gap-1.5">
                  <Label>Description</Label>
                  <Textarea
                    rows={3}
                    maxLength={4000}
                    placeholder="What needs doing, and anything the assignee should know."
                    {...form.register('description')}
                  />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <Field
                    label="Assignee"
                    required
                    error={form.formState.errors.assigneeId?.message}
                  >
                    <Select {...form.register('assigneeId', { required: 'Pick an assignee' })}>
                      <option value="">Select…</option>
                      {people.map((p) => (
                        <option key={p.id} value={p.id}>
                          {p.name}
                        </option>
                      ))}
                    </Select>
                  </Field>
                  <div className="flex flex-col gap-1.5">
                    <Label>Due date</Label>
                    <Input type="date" {...form.register('dueOn')} />
                  </div>
                </div>
                <div className="flex flex-col gap-1.5">
                  <Label>Parent task (optional)</Label>
                  <Select {...form.register('parentTaskId')}>
                    <option value="">None (top-level)</option>
                    {rootTasks.map((t) => (
                      <option key={t.id} value={t.id}>
                        {t.title}
                      </option>
                    ))}
                  </Select>
                </div>
                {errorMessage ? (
                  <Alert variant="destructive">
                    <AlertTitle>Create failed</AlertTitle>
                    <AlertDescription>{errorMessage}</AlertDescription>
                  </Alert>
                ) : null}
                <DialogFooter>
                  <Button type="submit" disabled={createMutation.isPending}>
                    {createMutation.isPending ? 'Saving…' : 'Create task'}
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <TaskBoard projectId={projectId} />
    </div>
  );
}

function currentMonth(): string {
  const now = new Date();
  const istMs = now.getTime() + (5 * 60 + 30) * 60 * 1000;
  const d = new Date(istMs);
  const y = d.getUTCFullYear();
  const m = String(d.getUTCMonth() + 1).padStart(2, '0');
  return `${y}-${m}`;
}

function AllocationsTab({ projectId }: { projectId: string }): JSX.Element {
  const [month, setMonth] = React.useState(currentMonth());
  const matrixQuery = useQuery({
    queryKey: ['allocations', month],
    queryFn: () => api.getAllocationMatrix(month),
  });
  const peopleQuery = useQuery({
    queryKey: ['people-simple'],
    queryFn: () => api.listPeopleSimple(),
  });

  const cells = matrixQuery.data?.cells ?? [];
  const people = peopleQuery.data ?? [];
  const projectCells = cells.filter((c) => c.projectId === projectId);
  const percentByPerson = new Map<string, number>();
  for (const c of projectCells) percentByPerson.set(c.personId, c.percent);

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-end gap-3">
        <div className="flex flex-col gap-1.5">
          <Label>Month</Label>
          <Input type="month" value={month} onChange={(e) => setMonth(e.currentTarget.value)} />
        </div>
      </div>
      <Card>
        <CardHeader>
          <CardTitle>Allocations for this project</CardTitle>
          <CardDescription>Read-only. Full editor on the Allocations page.</CardDescription>
        </CardHeader>
        <CardContent>
          {projectCells.length === 0 ? (
            <p className="text-sm text-[hsl(var(--muted-foreground))]">
              No people allocated to this project for {month}.
            </p>
          ) : (
            <ul className="flex flex-col divide-y divide-[hsl(var(--border))]">
              {people
                .filter((p) => percentByPerson.has(p.id))
                .map((p) => (
                  <li key={p.id} className="flex items-center justify-between gap-3 py-2 text-sm">
                    <span>{p.name}</span>
                    <Badge variant="muted">{percentByPerson.get(p.id) ?? 0}%</Badge>
                  </li>
                ))}
            </ul>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

type BudgetFormValues = {
  kind: BudgetKind;
  amountRupees: string;
  validFrom: string;
  reason: string;
};

type StatusFormValues = {
  to: ProjectStatus;
  reason: string;
};

function BudgetTab({ projectId }: { projectId: string }): JSX.Element {
  const queryClient = useQueryClient();
  const [budgetOpen, setBudgetOpen] = React.useState(false);
  const [statusOpen, setStatusOpen] = React.useState(false);
  const [errorMessage, setErrorMessage] = React.useState<string | null>(null);

  const projectQuery = useQuery({
    queryKey: ['project', projectId],
    queryFn: () => api.getProject(projectId),
  });

  const budgetForm = useForm<BudgetFormValues>({
    defaultValues: { kind: 'fee', amountRupees: '', validFrom: '', reason: '' },
  });

  const statusForm = useForm<StatusFormValues>({
    defaultValues: { to: 'active', reason: '' },
  });

  const budgetMutation = useMutation({
    mutationFn: (v: BudgetFormValues) => {
      const rupees = Number(v.amountRupees);
      if (!Number.isFinite(rupees) || rupees < 0) throw new Error('Amount must be non-negative');
      const amountPaise = Math.round(rupees * 100);
      return api.addProjectBudget(projectId, {
        kind: v.kind,
        amountPaise,
        validFrom: v.validFrom,
        reason: v.reason,
      });
    },
    onSuccess: async () => {
      setBudgetOpen(false);
      budgetForm.reset();
      setErrorMessage(null);
      await queryClient.invalidateQueries({ queryKey: ['project', projectId] });
    },
    onError: (err) => {
      setErrorMessage(toMessage(err));
    },
  });

  const toggleHoursMutation = useMutation({
    mutationFn: () => api.toggleActualHours(projectId),
    onSuccess: async () => {
      setErrorMessage(null);
      await queryClient.invalidateQueries({ queryKey: ['project', projectId] });
    },
    onError: (err) => setErrorMessage(toMessage(err)),
  });

  const statusMutation = useMutation({
    mutationFn: (v: StatusFormValues) =>
      api.changeProjectStatus(projectId, { to: v.to, reason: v.reason || undefined }),
    onSuccess: async () => {
      setStatusOpen(false);
      statusForm.reset();
      setErrorMessage(null);
      await queryClient.invalidateQueries({ queryKey: ['project', projectId] });
    },
    onError: (err) => {
      setErrorMessage(toMessage(err));
    },
  });

  const project = projectQuery.data;
  if (!project) return <p className="text-sm">Loading…</p>;

  return (
    <div className="flex flex-col gap-4">
      <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Current fee budget</CardTitle>
            <CardDescription>Effective today</CardDescription>
          </CardHeader>
          <CardContent>
            <p className="font-display text-3xl">
              {formatInr(paise(BigInt(project.currentFeePaise)))}
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Current cost budget</CardTitle>
            <CardDescription>Effective today</CardDescription>
          </CardHeader>
          <CardContent>
            <p className="font-display text-3xl">
              {formatInr(paise(BigInt(project.currentCostPaise)))}
            </p>
          </CardContent>
        </Card>
      </div>

      <div className="flex flex-wrap gap-2">
        <Dialog open={budgetOpen} onOpenChange={setBudgetOpen}>
          <DialogTrigger asChild>
            <Button size="sm">Add budget revision</Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>New budget revision</DialogTitle>
              <DialogDescription>
                Revisions never overwrite. Latest applies as of its validFrom.
              </DialogDescription>
            </DialogHeader>
            <form
              onSubmit={budgetForm.handleSubmit((v) => budgetMutation.mutate(v))}
              className="flex flex-col gap-3"
            >
              <div className="grid grid-cols-2 gap-3">
                <div className="flex flex-col gap-1.5">
                  <Label>Kind</Label>
                  <Select {...budgetForm.register('kind')}>
                    <option value="fee">Fee</option>
                    <option value="cost">Cost</option>
                  </Select>
                </div>
                <Field
                  label="Amount (INR)"
                  required
                  error={budgetForm.formState.errors.amountRupees?.message}
                >
                  <Input
                    type="number"
                    step="0.01"
                    min="0"
                    {...budgetForm.register('amountRupees', { required: 'Enter an amount' })}
                  />
                </Field>
                <Field
                  label="Valid from"
                  required
                  error={budgetForm.formState.errors.validFrom?.message}
                >
                  <Input
                    type="date"
                    {...budgetForm.register('validFrom', { required: 'Pick a date' })}
                  />
                </Field>
              </div>
              <Field label="Reason" required error={budgetForm.formState.errors.reason?.message}>
                <Input {...budgetForm.register('reason', { required: 'Give a reason' })} />
              </Field>
              {errorMessage ? (
                <Alert variant="destructive">
                  <AlertTitle>Failed</AlertTitle>
                  <AlertDescription>{errorMessage}</AlertDescription>
                </Alert>
              ) : null}
              <DialogFooter>
                <Button type="submit" disabled={budgetMutation.isPending}>
                  {budgetMutation.isPending ? 'Saving…' : 'Save revision'}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
        <Dialog open={statusOpen} onOpenChange={setStatusOpen}>
          <DialogTrigger asChild>
            <Button size="sm" variant="outline">
              Change status
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Change project status</DialogTitle>
              <DialogDescription>
                Closing requires zero open tasks. Reopening a closed project is Admin-only.
              </DialogDescription>
            </DialogHeader>
            <form
              onSubmit={statusForm.handleSubmit((v) => statusMutation.mutate(v))}
              className="flex flex-col gap-3"
            >
              <div className="flex flex-col gap-1.5">
                <Label>New status</Label>
                <Select {...statusForm.register('to')}>
                  <option value="active">Active</option>
                  <option value="on_hold">On hold</option>
                  <option value="closed">Closed</option>
                </Select>
              </div>
              <div className="flex flex-col gap-1.5">
                <Label>Reason</Label>
                <Input {...statusForm.register('reason')} placeholder="Optional context" />
              </div>
              {errorMessage ? (
                <Alert variant="destructive">
                  <AlertTitle>Failed</AlertTitle>
                  <AlertDescription>{errorMessage}</AlertDescription>
                </Alert>
              ) : null}
              <DialogFooter>
                <Button type="submit" disabled={statusMutation.isPending}>
                  {statusMutation.isPending ? 'Saving…' : 'Change status'}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Actual hours</CardTitle>
          <CardDescription>
            When on, minutes declared in the work log roll up into each task's actual hours. The
            work log always records the split; this only controls whether it shows on tasks.
          </CardDescription>
        </CardHeader>
        <CardContent className="flex items-center justify-between gap-3">
          <Badge variant={project.actualHoursOn ? 'default' : 'muted'}>
            {project.actualHoursOn ? 'Tracking on' : 'Tracking off'}
          </Badge>
          <Button
            variant="outline"
            size="sm"
            disabled={toggleHoursMutation.isPending}
            onClick={() => toggleHoursMutation.mutate()}
          >
            {project.actualHoursOn ? 'Turn off' : 'Turn on'}
          </Button>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Budget revisions</CardTitle>
          <CardDescription>{project.budgetRevisions.length} total</CardDescription>
        </CardHeader>
        <CardContent>
          {project.budgetRevisions.length === 0 ? (
            <p className="text-sm text-[hsl(var(--muted-foreground))]">No revisions yet.</p>
          ) : (
            <ul className="flex flex-col divide-y divide-[hsl(var(--border))]">
              {project.budgetRevisions.map((r) => (
                <li key={r.id} className="flex items-center justify-between gap-3 py-3 text-sm">
                  <div>
                    <div className="font-medium">
                      {r.kind.toUpperCase()} · {formatInr(paise(BigInt(r.amountPaise)))}
                    </div>
                    <div className="text-xs text-[hsl(var(--muted-foreground))]">
                      valid from {r.validFrom} · {r.reason}
                    </div>
                  </div>
                  <Badge variant="muted">{r.kind}</Badge>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
